import React, { useCallback } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { useZodForm } from '@/shared/hooks/useZodForm.ts';
import { BusinessCase, PRODUCT_TYPES, SUPPORTED_UNITS, SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import {
    CustomProduct,
    CustomProductBase,
    CustomProductFormRule,
    getCustomProductsFormSchema,
} from '@/shared/forms/CustomProductsForm/schemas/getCustomProductsFormSchema.ts';
import { useTranslation } from 'react-i18next';
import { NewCustomRequestProduct } from '@/shared/forms/CustomProductsForm/components/NewCustomRequestProduct.tsx';
import { WizardFooter } from '@/shared/components/Wizard.tsx';
import { DeleteAllProductsButton } from '@/shared/components/DeleteAllProductsButton.tsx';
import { Button } from '@schuettflix/planum-react';
import { isWasteProduct } from '@/shared/domain/product/isWasteProduct.ts';
import { AddCustomProductButton } from '@/shared/forms/CustomProductsForm/components/AddCustomProductButton.tsx';
import { getProductCategoryByProductType } from '@schuettflix/product-group-utils';
import { ProductConfigurationScope } from '@/shared/types/ProductConfigurationScope.ts';
import { useFormattedActiveTaxClassesByMarket } from '@/clients/tax-classes/useTaxClasses.ts';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';
import { MARKET_CODE_TO_CURRENCY_CODE_MAP } from '@schuettflix/utils';
import { PRODUCT_NAME_LABEL } from '@/phrase/ProductNameLabels.ts';
import { productTypeToUnitMap } from '@/shared/domain/product/productTypeToUnitMap.ts';
import { resolveBusinessCaseProductConfiguration } from '@/shared/forms/CustomProductsForm/utils/resolveBusinessCaseProductConfiguration.ts';
import { Nullable } from '@/shared/types/Nullable.ts';

type CustomProductsFormProps = {
    scope: ProductConfigurationScope;
    hideDeleteAllProductsButton?: boolean;
    formRules?: {
        disableAllProductsUnits?: boolean;
        customProducts?: {
            [identifier: string]: CustomProductFormRule;
        };
    };
    businessCase: BusinessCase;
    defaultValues?: Partial<CustomProduct>[];
    defaultBillingUnit?: SUPPORTED_UNITS;
    onSubmit: (customProducts: CustomProduct[]) => void;
    onClearSelection: () => Promise<void> | void;
    submitButtonLabel: string;
    customerOrganizationId: number;
    mode: 'create' | 'edit';
};

export const CustomProductsForms = ({
    scope,
    hideDeleteAllProductsButton,
    formRules,
    businessCase,
    defaultValues,
    defaultBillingUnit,
    onSubmit,
    onClearSelection,
    submitButtonLabel,
    customerOrganizationId,
    mode,
}: CustomProductsFormProps) => {
    const { t } = useTranslation();
    const { defaultTaxClass, taxClassOptions } = useFormattedActiveTaxClassesByMarket();
    const { marketCode } = useMarketContext();

    const MAIN_MATERIAL_PRODUCT_INDEX = 0;
    const MAIN_TRANSPORT_PRODUCT_INDEX = 1;

    const { defaultBusinessCaseCustomProducts, supportedProducts } = resolveBusinessCaseProductConfiguration({
        businessCase,
        marketCode,
        defaultTaxClass,
        defaultBillingUnit,
    });

    const defaultCustomProducts = defaultValues ?? defaultBusinessCaseCustomProducts;

    const methods = useZodForm({
        schema: getCustomProductsFormSchema({
            scope,
            errorMessages: {
                required: t('product.addPosition.errorMessage.pleaseEnterValue'),
                tooSmallQuantity: t('forms.inputs.quantity.minValue.errorMessage'),
                francoSalesPriceSignMismatch: t('forms.inputs.salesPrice.francoSignMismatch.errorMessage'),
            },
            formRules,
            pricingStrategy: businessCase.pricingStrategy,
        }),
        defaultValues: {
            customProducts: defaultCustomProducts,
        },
    });

    const { control, register, handleSubmit, getValues } = methods;

    const { fields, remove, append, update } = useFieldArray({
        control,
        name: 'customProducts',
    });

    const onUpdateProductField = <T extends keyof CustomProduct, F extends CustomProduct[T]>(
        fieldName: T,
        value: F
    ) => {
        switch (businessCase.pricingStrategy) {
            case 'STANDARD':
                return;
            case 'FRANCO': {
                if (fieldName === 'unit' || fieldName === 'quantity') {
                    const transportProduct = getValues(`customProducts.${MAIN_TRANSPORT_PRODUCT_INDEX}`);
                    update(MAIN_TRANSPORT_PRODUCT_INDEX, {
                        ...transportProduct,
                        [fieldName]: value,
                    });
                }
                break;
            }
            case 'MERCHANT': {
                if (fieldName !== 'purchasePrice' && fieldName !== 'salesPrice') {
                    const transportProduct = getValues(`customProducts.${MAIN_TRANSPORT_PRODUCT_INDEX}`);
                    update(MAIN_TRANSPORT_PRODUCT_INDEX, {
                        ...transportProduct,
                        [fieldName]: value,
                    });
                }
                break;
            }
        }
    };

    const handleFormSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
        async event => {
            event.preventDefault();
            await handleSubmit(({ customProducts }) => {
                onSubmit(customProducts);
            }, console.error)();
        },
        [handleSubmit, onSubmit]
    );

    return (
        <FormProvider {...methods}>
            <form
                className="@container/material-product flex flex-1 flex-col justify-between"
                data-test="custom-request-product-configurations-form"
                onSubmit={handleFormSubmit}
            >
                <div className="mt-6 flex flex-col gap-6">
                    {fields.map((field, index) => {
                        const isMainTransportMerchantProduct =
                            index === MAIN_TRANSPORT_PRODUCT_INDEX && businessCase.pricingStrategy === 'MERCHANT';
                        const isMainTransportFrancoProduct =
                            index === MAIN_TRANSPORT_PRODUCT_INDEX && businessCase.pricingStrategy === 'FRANCO';
                        const disableDelete =
                            index === MAIN_MATERIAL_PRODUCT_INDEX ||
                            (index === MAIN_TRANSPORT_PRODUCT_INDEX && !businessCase.isStandalone) ||
                            (field.identifier !== undefined &&
                                !!formRules?.customProducts?.[field.identifier]?.disableDelete);
                        const disableUnit =
                            isMainTransportMerchantProduct ||
                            isMainTransportFrancoProduct ||
                            !!formRules?.disableAllProductsUnits ||
                            (field.identifier !== undefined &&
                                !!formRules?.customProducts?.[field.identifier]?.disableUnit);

                        const showFrancoHint =
                            businessCase.pricingStrategy !== 'STANDARD' &&
                            (index === MAIN_MATERIAL_PRODUCT_INDEX || index === MAIN_TRANSPORT_PRODUCT_INDEX);
                        const showNonFrancoHint =
                            businessCase.pricingStrategy !== 'STANDARD' &&
                            index !== MAIN_MATERIAL_PRODUCT_INDEX &&
                            index !== MAIN_TRANSPORT_PRODUCT_INDEX;

                        register(`customProducts.${index}.scope`, {
                            value: scope,
                        });

                        return (
                            <NewCustomRequestProduct
                                key={field.id}
                                customerOrganizationId={customerOrganizationId}
                                scope={scope}
                                productType={field.productType}
                                productCategory={field.category}
                                disableDelete={disableDelete}
                                showFrancoHint={showFrancoHint}
                                showNonFrancoHint={showNonFrancoHint}
                                onDeleteCustomProduct={() => remove(index)}
                                onUpdateField={(
                                    fieldName: keyof CustomProduct,
                                    value: CustomProduct[keyof CustomProduct]
                                ) => {
                                    if (index === MAIN_MATERIAL_PRODUCT_INDEX) {
                                        onUpdateProductField(fieldName, value);
                                    }
                                }}
                                productNameProps={{
                                    ...register(`customProducts.${index}.productName`),
                                    disabled: isMainTransportMerchantProduct,
                                }}
                                freightTypeProps={{
                                    ...register(`customProducts.${index}.freightType`),
                                    disabled: isMainTransportMerchantProduct || !businessCase.isStandalone,
                                }}
                                disposalPurposeProps={{
                                    ...register(`customProducts.${index}.disposalPurpose`),
                                    disabled:
                                        isMainTransportMerchantProduct || isWasteProduct(businessCase.productType),
                                }}
                                quantityProps={{
                                    ...register(`customProducts.${index}.quantity`),
                                    disabled: isMainTransportMerchantProduct || isMainTransportFrancoProduct,
                                }}
                                unitProps={{
                                    ...register(`customProducts.${index}.unit`),
                                    disabled: disableUnit,
                                    supportedUnitNames: [...SUPPORTED_UNITS_SCHEMA.options],
                                }}
                                partnerSelectionProps={{
                                    ...register(`customProducts.${index}.partnerOrganizationId`),
                                    defaultValue: defaultValues?.[index]?.partnerOrganizationId,
                                    disabled: isMainTransportMerchantProduct,
                                }}
                                purchasePriceProps={{
                                    ...register(`customProducts.${index}.purchasePrice`),
                                    disabled: isMainTransportMerchantProduct,
                                }}
                                purchaseTaxClassIdProps={{
                                    ...register(`customProducts.${index}.purchaseTaxClassId`),
                                    disabled: isMainTransportMerchantProduct,
                                    options: taxClassOptions,
                                }}
                                salesPriceProps={{
                                    ...register(`customProducts.${index}.salesPrice`),
                                    disabled: isMainTransportMerchantProduct,
                                }}
                                salesTaxClassIdProps={{
                                    ...register(`customProducts.${index}.salesTaxClassId`),
                                    disabled: isMainTransportMerchantProduct,
                                    options: taxClassOptions,
                                }}
                                serviceDateProps={{
                                    ...register(`customProducts.${index}.serviceDate`),
                                    disabled: isMainTransportMerchantProduct,
                                }}
                                showZeroPurchasePriceProps={{
                                    ...register(`customProducts.${index}.showZeroPurchasePrice`),
                                    disabled: isMainTransportMerchantProduct,
                                }}
                                showZeroSalesPriceProps={{
                                    ...register(`customProducts.${index}.showZeroSalesPrice`),
                                    disabled: isMainTransportMerchantProduct,
                                }}
                                currencyCodeProps={{
                                    ...register(`customProducts.${index}.currencyCode`),
                                }}
                            />
                        );
                    })}

                    <AddCustomProductButton
                        supportedProducts={supportedProducts}
                        onAddProduct={(productType: PRODUCT_TYPES) =>
                            append({
                                productType,
                                scope,
                                category: getProductCategoryByProductType(productType),
                                currencyCode: MARKET_CODE_TO_CURRENCY_CODE_MAP[marketCode],
                                purchaseTaxClassId: defaultTaxClass,
                                salesTaxClassId: defaultTaxClass,
                                productName: t(PRODUCT_NAME_LABEL[productType]),
                                unit: productTypeToUnitMap[productType] ?? defaultBillingUnit,
                                showZeroPurchasePrice: false,
                                showZeroSalesPrice: false,
                                // NOTE: RHF v7.54.2 doesn't leave a field empty when append() gets `undefined`
                                // for a key — it falls back to a stale value (e.g. leaks the previous row's default).
                                // Passing `null` avoids that, since RHF treats it as an explicit value.
                                quantity: null,
                                purchasePrice: null,
                                salesPrice: null,
                                partnerOrganizationId: null,
                                // `unknown` bypasses the structural check for this assertion — CustomProduct's fields reject
                                // `null`, but that's the exact shape RHF's stale-default limitation (above) requires until the row is populated.
                            } satisfies Nullable<Partial<CustomProductBase>> as unknown as CustomProduct)
                        }
                    />
                </div>

                <WizardFooter className="mt-6">
                    <div className="flex w-full items-center justify-between">
                        <div>
                            {!hideDeleteAllProductsButton && (
                                <DeleteAllProductsButton onDeleteAsync={async () => await onClearSelection()} />
                            )}
                        </div>
                        <Button variant={mode === 'create' ? 'primary' : 'secondary'} type="submit" data-test="proceed">
                            {submitButtonLabel}
                        </Button>
                    </div>
                </WizardFooter>
            </form>
        </FormProvider>
    );
};
